import React from 'react';
import type { MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { Theme } from '@material-ui/core';
import {
    Divider,
    ListItem,
    ListItemText,
    ListSubheader,
} from '@material-ui/core';
import { showToast } from '@klpkonsern/ui';

import {
    Block,
    Typography,
    IconName,
    Illustration,
    Kommunikasjon,
} from '@klp/design';
import { useSendReminderMutation } from '../../services/rtkApi';
import { prosjektActions } from '../../store/prosjektSlice';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { useRoleMapping } from '@klp/prosjekt/hooks';

export type MenuPopperCurrentActivityProps = {
    isCurrentActivitySet?: boolean | undefined;
    prosessInstansId: string | undefined;
    role?: string;
    /** Person holding the owner role on the process, shown in the header */
    roleHolder?: string;
    /** Person the current task is assigned to, who receives the reminder */
    taskAssignee?: string;
};

const styles = (theme: Theme) => ({
    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        paddingRight: theme.spacing(2),
    },
});

export const MenuPopperCurrentActivity = ({
    isCurrentActivitySet = false,
    prosessInstansId,
    role,
    roleHolder,
    taskAssignee,
}: MenuPopperCurrentActivityProps) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { handleMapping: handleRoleMapping } = useRoleMapping();

    const dispatch = useDispatch();
    const [sendReminder, { isLoading }] = useSendReminderMutation();
    const { setActiveModalProsessInstansId } = prosjektActions;

    const handleSendReminder = async (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();

        if (!prosessInstansId) return;

        await sendReminder({ prosessInstansId })
            .unwrap()
            .then(() => {
                showToast({
                    body: t('prosjekt.successfulOperation'),
                    type: 'success',
                });
            })
            .catch(() => {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
    };

    const handleAssignResponsibleUser = (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        dispatch(setActiveModalProsessInstansId(prosessInstansId));
    };

    if (!isCurrentActivitySet) return null;

    const roleLabel = handleRoleMapping(role);
    const headerLabel =
        roleLabel ?? t('prosjekt.table.tildelAnsvarlig.ansvarlig');

    return (
        <>
            <Divider />
            <ListSubheader disableSticky={true}>
                <Typography>
                    {roleHolder ? `${headerLabel}: ${roleHolder}` : headerLabel}
                </Typography>
            </ListSubheader>
            <ListItem
                dense={true}
                button={true}
                disabled={isLoading}
                onClick={handleSendReminder}
            >
                <Block className={classes.iconContainer}>
                    <Illustration pictogram={Kommunikasjon.EPOST} />
                </Block>
                <ListItemText>
                    {taskAssignee ? (
                        <Typography
                            translation="prosjekt.table.actionMenuItems.sendReminderName"
                            translationOptions={{ name: taskAssignee }}
                        />
                    ) : (
                        <Typography translation="prosjekt.table.actionMenuItems.sendReminder" />
                    )}
                </ListItemText>
            </ListItem>
            <ListItem
                dense={true}
                button={true}
                onClick={handleAssignResponsibleUser}
            >
                <Block className={classes.iconContainer}>
                    <Illustration icon={IconName.PROFILE} />
                </Block>
                <ListItemText>
                    {roleLabel ? (
                        <Typography
                            translation="prosjekt.table.actionMenuItems.tildelRolle"
                            translationOptions={{ rolle: roleLabel }}
                        />
                    ) : (
                        <Typography translation="prosjekt.table.actionMenuItems.tildel" />
                    )}
                </ListItemText>
            </ListItem>
        </>
    );
};
